/**
 * MER-521: the bare-domain "/" routing decision as a PURE state machine, so the
 * node:test suite can exercise the full auth matrix without a browser (the
 * Playwright + ephemeral-Supabase E2E that drives real sessions is deferred;
 * this pins the decision logic that the E2E would otherwise be the only guard
 * for). RootResolver renders from this; the async tenant_members lookup + the
 * captured-callback snapshots stay in the component.
 *
 * Precedence (each step wins over the ones below; order is load-bearing and
 * mirrors RootResolver exactly):
 *   1. isLoading                 -> skeleton          (useAuth still exchanging
 *                                                       the URL hash into a
 *                                                       session; deciding now
 *                                                       would drop the hash)
 *   2. recoveryFlow (hash)       -> redirect /set-password   (MER-519)
 *   3. tokenHashFlow (query)     -> redirect /set-password+search (MER-523)
 *   4. no session                -> authError ? AuthError(code) : ComingSoon(signIn)
 *   5. staff                     -> redirect /admin
 *   6. membership still resolving (slug === undefined) -> skeleton
 *   7. needsWorkspaceChoice      -> redirect /choose-workspace   (MER-532: >1
 *                                       active membership, no active/default)
 *   8. slug -> /<slug>, else ComingSoon (signed in, unprovisioned; no Sign in)
 *
 * Steps 2/3 run AFTER isLoading (1) on purpose: a valid hash link must finish
 * its detectSessionInUrl exchange before we navigate away. Step 4 gates the
 * auth-error on no-session so a stale callback hash on an already-signed-in tab
 * resolves to the dashboard instead of an error (and avoids a post-login loop).
 *
 * MER-532: the home slug vs. picker decision is computed by resolveWorkspace
 * (lib/workspaceRoute.mjs) in RootResolver; this state machine just receives
 * the resolved `slug` (the chosen single home, or null) plus the
 * `needsWorkspaceChoice` flag and routes the picker. `slug === undefined` still
 * means "memberships not resolved yet" -> skeleton; when the picker is needed
 * the caller passes `slug: null` so step 6 falls through to step 7.
 */

import { homePathFor } from "./homePath.mjs";

/**
 * @param {{
 *   isLoading: boolean,
 *   authError: { code: string | null } | null,
 *   recoveryFlow: "recovery" | "invite" | null,
 *   tokenHashFlow: { tokenHash: string, type: "recovery" | "invite" } | null,
 *   search: string,
 *   hasSession: boolean,
 *   staff: boolean,
 *   slug: string | null | undefined,
 *   needsWorkspaceChoice?: boolean,
 * }} state
 * @returns {{ kind: "skeleton" }
 *   | { kind: "redirect", to: string }
 *   | { kind: "authError", code: string | null }
 *   | { kind: "comingSoon", showSignIn: boolean }}
 */
export function resolveRoot(state) {
  const { isLoading, authError, recoveryFlow, tokenHashFlow, search, hasSession, staff, slug } =
    state;
  const needsWorkspaceChoice = state.needsWorkspaceChoice ?? false;

  if (isLoading) return { kind: "skeleton" };
  if (recoveryFlow) return { kind: "redirect", to: "/set-password" };
  if (tokenHashFlow) return { kind: "redirect", to: `/set-password${search ?? ""}` };
  if (!hasSession) {
    return authError
      ? { kind: "authError", code: authError.code }
      : { kind: "comingSoon", showSignIn: true };
  }
  if (staff) return { kind: "redirect", to: "/admin" };
  if (slug === undefined) return { kind: "skeleton" };
  // MER-532: >1 active membership with no active/default choice -> interstitial.
  if (needsWorkspaceChoice) return { kind: "redirect", to: "/choose-workspace" };
  const dest = homePathFor({ isStaff: false, slug });
  if (dest) return { kind: "redirect", to: dest };
  // Signed in but no tenant: hold on ComingSoon, but hide the Sign in link
  // (they're already authed, so it would just bounce back to "/").
  return { kind: "comingSoon", showSignIn: false };
}
